import './TrackmanParametersListV2.scss';

import { Input, Select, Table, TableHead, TableRow, TableCell, TableBody } from '@trackman/web-shared-components';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import Paragraph from 'components/TextComps/Paragraph';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminPagesRelativeRoutes, DefaultRoutes } from 'Routes';
import { ValidSortProp, dataSorter } from 'utilities/dataSorters';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { ReactComponent as SearchIcon } from 'images/icons/Search.svg';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { useTrackmanParametersV2 } from 'queryHooks/Admin/TrackmanParametersV2/useTrackmanParametersV2';
import { useDeleteTrackmanParameterV2 } from 'queryHooks/Admin/TrackmanParametersV2/useDeleteTrackmanParameterV2';
import { useTrackmanParameterCategories } from 'queryHooks/Admin/TrackmanParameterCategories/useTrackmanParameterCategories';
import { useParametersFiltersStore } from 'stores/Admin/Editors/ParametersFiltersStore';
import { TrackmanParameterV2Overview } from 'types/admin/trackmanParametersV2';
import { mapBooleanToString } from 'utilities/booleanMapper';
import dayjs from 'dayjs';
import { TranslationStatusChip } from 'pages/Admin/ModulesV2/TranslationStatusChip/TranslationStatusChip';

type TrackmanParameterV2ListItem = TrackmanParameterV2Overview & {
  primaryCategory: string;
};

export const TrackmanParametersListV2 = () => {
  useSetPageTitle({ title: 'Trackman Parameters List' });
  const navigate = useNavigate();

  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<ValidSortProp<TrackmanParameterV2ListItem>>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');
  const [selectedParameterId, setSelectedParameterId] = useState('');

  const filterByName = useParametersFiltersStore((state) => state.filterByName);
  const filterByCategory = useParametersFiltersStore((state) => state.filterByCategory);
  const filterByVisibility = useParametersFiltersStore((state) => state.filterByVisibility);
  const setFilterByName = useParametersFiltersStore((state) => state.setFilterByName);
  const setFilterByCategory = useParametersFiltersStore((state) => state.setFilterByCategory);
  const setFilterByVisibility = useParametersFiltersStore((state) => state.setFilterByVisibility);

  const { mutate: deleteParameter, isPending: isDeletePending } = useDeleteTrackmanParameterV2();
  const { data: parameters = [], isPending: parametersPending } = useTrackmanParametersV2();
  const { data: categories = [], isPending: categoriesPending } = useTrackmanParameterCategories();

  const categoryTitleById = useMemo(() => new Map(categories.map((category) => [category.id, category.title])), [categories]);

  const listItems = useMemo<TrackmanParameterV2ListItem[]>(
    () =>
      parameters.map((parameter) => ({
        ...parameter,
        primaryCategory: parameter.categoryId ? (categoryTitleById.get(parameter.categoryId) ?? 'Unknown category') : 'Unassigned',
      })),
    [categoryTitleById, parameters]
  );

  const uniquePrimaryCategories = useMemo(() => ['None', ...new Set(listItems.map((parameter) => parameter.primaryCategory))], [listItems]);

  const filteredParametersList = useMemo(() => {
    let filtered = [...listItems];

    if (filterByName) {
      filtered = filtered.filter((parameter) => parameter.title.toLowerCase().includes(filterByName.toLowerCase()));
    }

    if (filterByCategory && filterByCategory !== 'None') {
      filtered = filtered.filter((parameter) => parameter.primaryCategory.toLowerCase().includes(filterByCategory.toLowerCase()));
    }

    if (filterByVisibility && filterByVisibility !== 'None') {
      filtered = filtered.filter((parameter) =>
        mapBooleanToString(parameter.visible).toLowerCase().includes(filterByVisibility.toLowerCase())
      );
    }

    return dataSorter(filtered, sortByProperty, sortOrder);
  }, [filterByCategory, filterByName, filterByVisibility, listItems, sortByProperty, sortOrder]);

  const handleSortChange = (property: ValidSortProp<TrackmanParameterV2ListItem>, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property);
    setSortOrder(order);
  };

  const handleDelete = () => {
    deleteParameter(selectedParameterId, { onSuccess: () => setIsModalOn(false) });
  };

  const isPending = parametersPending || categoriesPending;

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Trackman Parameters List</h1>
      </div>
      <div className='admin-content-container'>
        <div className='parameters-list-header'>
          <div className='parameters-searchbar'>
            <div className='parameters-search parameters-search--input'>
              <Input placeholder='Search by name' value={filterByName} onChange={(e) => setFilterByName(e.currentTarget.value)} />
              <SearchIcon />
            </div>

            <div className='parameters-search'>
              <Select
                label='Select primary category'
                value={filterByCategory}
                selectOptions={uniquePrimaryCategories.map((category) => {
                  return {
                    label: category,
                    value: category,
                  };
                })}
                onChange={(e) => setFilterByCategory(e.target.value as string)}
              />
            </div>

            <div className='parameters-search'>
              <Select
                label='Select visibility status'
                value={filterByVisibility}
                selectOptions={['None', 'Yes', 'No'].map((status) => {
                  return {
                    label: status,
                    value: status,
                  };
                })}
                onChange={(e) => setFilterByVisibility(e.target.value as string)}
              />
            </div>
          </div>
          <div className='parameters-list-header-actions'>
            <Link to={`/Admin/${AdminPagesRelativeRoutes.TrackmanParameterCategories}`} className='styled-button link-button tertiary'>
              <span>Manage categories</span>
            </Link>
            <HeaderCreateButton
              buttonTitle='New parameter'
              onClick={() => navigate(`${DefaultRoutes.Admin}/${AdminPagesRelativeRoutes.TrackmanParametersEditorV2}`)}
            />
          </div>
        </div>

        <div className='tmu-table-wrapper'>
          <Table className='tmu-table admin-table'>
            <TableHead>
              <TableRow>
                <TableCell>
                  Name
                  <SortOrderButton
                    isActive={sortByProperty === 'title'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                  />
                </TableCell>
                <TableCell>Translation status</TableCell>
                <TableCell>
                  Primary Category
                  <SortOrderButton
                    isActive={sortByProperty === 'primaryCategory'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('primaryCategory', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Visible
                  <SortOrderButton
                    isActive={sortByProperty === 'visible'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('visible', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Date created
                  <SortOrderButton
                    isActive={sortByProperty === 'createdAt'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                  />
                </TableCell>
                <TableCell>
                  Last edited
                  <SortOrderButton
                    isActive={sortByProperty === 'updatedAt'}
                    sortOrder={sortOrder}
                    onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                  />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Paragraph>
                      <>Loading parameters…</>
                    </Paragraph>
                  </TableCell>
                </TableRow>
              ) : filteredParametersList.length ? (
                filteredParametersList.map((row) => {
                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>
                        <TranslationStatusChip status={row.translationStatus} />
                      </TableCell>
                      <TableCell>{row.primaryCategory}</TableCell>
                      <TableCell>{mapBooleanToString(row.visible)}</TableCell>
                      <TableCell>{row.createdAt ? dayjs(row.createdAt).format('MMM DD, YYYY') : ''}</TableCell>
                      <TableCell>{row.updatedAt ? dayjs(row.updatedAt).format('MMM DD, YYYY') : ''}</TableCell>
                      <TableCell>
                        <div className='action-buttons'>
                          <Link to={`/Admin/${AdminPagesRelativeRoutes.TrackmanParametersEditorV2}/${row.id}`} className='row-button'>
                            <PencilIcon />
                          </Link>
                          <button
                            className={`row-button ${isDeletePending ? 'disabled' : ''}`}
                            onClick={() => {
                              setSelectedParameterId(row.id);
                              setIsModalOn(true);
                            }}
                            disabled={isDeletePending}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Paragraph>
                      <>No parameters found</>
                    </Paragraph>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={handleDelete} itemName='Trackman Parameter' />
      </div>
    </>
  );
};
